'use client';

import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import Link from 'next/link';
import Image from 'next/image';
import { X } from 'lucide-react';
import { usePostHog } from 'posthog-js/react';
import { useLocale, useTranslations } from 'next-intl';
import { sanitizeUserInput } from '@/utils/security';
import { Button } from '@/components/ui/Button';
import YesNoOracleWidget from '@/components/oracle/yes-no-oracle-widget';
import TempleKeeperBadge from '@/components/oracle/temple-keeper-badge';
import { useCSRF } from '@/contexts/csrf-context';
import { HeroUsageProvider, useHeroUsage } from '@/contexts/hero-usage-context';
import { useUsageFreshness } from '@/hooks/useUsageFreshness';
import { usageMetadataPropType } from '@/components/oracle/usage-indicator.types';
import { getRandomPlaceholderQuestion, formatPlaceholder, getRandomOracleCardPlaceholder, formatOracleCardPlaceholder } from '@/lib/questionsData';
import { useUserSession } from '@/hooks/use-user-session';
import { useStripeCheckout } from '@/hooks/use-stripe-checkout';
import { firebaseAuth as auth } from '@/libs/firebase/config';
import { signInWithGoogle } from '@/libs/firebase/auth';

const DECEMBER_PROMO_PRICE_ID = process.env.NEXT_PUBLIC_STRIPE_PRICE_ID_PREMIUM_24H_DECEMBER_PROMO;

const renderLinkTemplate = (template, linkHref) => {
  if (!template) return null;
  const [before, linkAndAfter] = template.split('{{link}}');
  if (!linkAndAfter) return template;
  const [linkText, after] = linkAndAfter.split('{{/link}}');

  return (
    <>
      {before}
      <Link
        href={linkHref}
        className="font-semibold text-purple-400 hover:text-purple-300 underline transition-colors"
      >
        {linkText}
      </Link>
      {after}
    </>
  );
};

const HeroStaticCopy = React.memo(function HeroStaticCopy({ badge, headline, subheadline, metaItems, metaLabel }) {
  return (
    <div className="w-full md:w-2/5 lg:w-2/5 flex flex-col space-y-6 pr-0 md:pr-8 lg:pr-12" data-testid="hero-static-copy">
      <h1 className="text-xs font-semibold tracking-[0.35em] text-[#8A8A8A]/70">
        {badge}
      </h1>
      <p
        className="text-3xl md:text-4xl lg:text-5xl text-white mt-4 font-marcellus font-light"
        style={{
          fontFamily: 'var(--font-marcellus)',
          fontWeight: 300,
          letterSpacing: '0.02em'
        }}
        data-testid="hero-headline"
      >
        {headline}
      </p>
      <h2 className="text-lg md:text-xl text-gray-300 mt-4 leading-relaxed">
        {subheadline}
      </h2>
      {metaItems.length > 0 && (
        <ul className="text-sm text-gray-400 mt-3 space-y-2" aria-label={metaLabel}>
          {metaItems.map((item) => (
            <li key={item} className="flex items-start">
              <span className="mt-0.5 text-[#D4B6FF]" aria-hidden="true">✧</span>
              <span className="ml-2 leading-relaxed">{item}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
});

HeroStaticCopy.displayName = 'HeroStaticCopy';
HeroStaticCopy.propTypes = {
  badge: PropTypes.string.isRequired,
  headline: PropTypes.string.isRequired,
  subheadline: PropTypes.string.isRequired,
  metaItems: PropTypes.arrayOf(PropTypes.string).isRequired,
  metaLabel: PropTypes.string.isRequired,
};

const HeroSectionContent = ({
  content = {},
  initialQuestion: initialQuestionProp = '',
  prefetchedAt = null,
  showOracleMovedToast = false,
  customWidget = null, // NEW: Optional custom widget component
  apiEndpoint = '/api/oracle/yes-no', // NEW: Optional custom API endpoint
  usageApiEndpoint = '/api/usage/free-yesno', // NEW: Optional custom usage API endpoint
  widgetTitleFree = null, // NEW: Optional widget title for free users
  widgetTitlePremium = null, // NEW: Optional widget title for premium users
}) => {
  const locale = useLocale();
  const t = useTranslations('home.hero');
  const {
    usageMetadata,
    userTier,
    dailyUsesRemaining,
    dailyLimitReached,
    forceUpgradeModal,
    hasInitialUsage,
    isPremiumTier,
    applyUsageSnapshot,
    clearUsageState,
    markLimitReached,
    dismissUpgradeModal,
    requestUpgradeModal
  } = useHeroUsage();

  const {
    widgetTitle: contentWidgetTitle = t('widgetTitle', { defaultValue: 'Get a FREE Yes or No Reading by the Oracle' }),
    widgetTitlePremium: contentWidgetTitlePremium = t('widgetTitlePremium', { defaultValue: 'Unlimited yes/no Oracle Readings' }),
    badge = t('badge', { defaultValue: "The #1 Yes or No Oracle for Life's Deeper Questions" }),
    headline = t('headline', { defaultValue: '🔮 Ask your question and receive divine guidance in seconds.' }),
    subheadline = t('subheadline', { defaultValue: 'Accurate online Oracle reading to help you find clarity.' }),
    meta = {},
    cta = {},
    limit = {},
    followUp = {},
    errors: errorMessages = {}
  } = content;

  // Use provided widget titles or fall back to content translations
  const widgetTitle = widgetTitleFree || contentWidgetTitle;
  const widgetTitlePremiumText = widgetTitlePremium || contentWidgetTitlePremium;
  const oracleMovedToast = content?.oracleMovedToast ?? {};
  const followUpCtas = followUp?.cta ?? {};
  const followUpAfter = followUp?.after ?? {};
  const metaItems = useMemo(() => {
    if (Array.isArray(meta?.items)) {
      return meta.items.filter(Boolean);
    }
    return Object.values(meta ?? {}).filter((value) => typeof value === 'string' && value.trim().length > 0);
  }, [meta]);
  const metaLabel = useMemo(() => t('metaLabel', { defaultValue: 'Key benefits' }), [t]);

  const [initialQuestion, setInitialQuestion] = useState(initialQuestionProp);
  const [yesNoAnswer, setYesNoAnswer] = useState(null);
  const [reframedQuestion, setReframedQuestion] = useState('');
  const [followUpQuestion, setFollowUpQuestion] = useState('');
  const [followUpAnswer, setFollowUpAnswer] = useState('');
  const [displayedFollowUpAnswer, setDisplayedFollowUpAnswer] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isFollowUpLoading, setIsFollowUpLoading] = useState(false);
  const [showFollowUp, setShowFollowUp] = useState(false);
  const [followUpTypingComplete, setFollowUpTypingComplete] = useState(false);
  const [error, setError] = useState(null);
  const [followUpError, setFollowUpError] = useState(null);
  const [deeperInsight, setDeeperInsight] = useState('');
  const [confidence, setConfidence] = useState(null);
  const [celebratePremium, setCelebratePremium] = useState(false);
  const [oracleMovedToastVisible, setOracleMovedToastVisible] = useState(Boolean(showOracleMovedToast));
  const [placeholderQuestion, setPlaceholderQuestion] = useState(null);
  const userId = useUserSession();
  const {
    loading: checkoutLoading,
    error: checkoutError,
    startCheckout,
    reset: resetCheckout
  } = useStripeCheckout();

  const followUpTypingIntervalRef = useRef(null);
  const pollingIntervalRef = useRef(null);
  const usageEtagRef = useRef(null);
  const heroImpressionLoggedRef = useRef(false);
  const previousModalOpenRef = useRef(forceUpgradeModal);
  const modalErrorLoggedRef = useRef(false);
  const previewLoggedRef = useRef(null);
  const isPrefetchFreshRef = useRef(false);
  const posthog = usePostHog();
  const { csrfToken, loading: csrfLoading } = useCSRF();
  const [usageFetchError, setUsageFetchError] = useState(null);
  // Use oracle card format for daily card tool, yes/no format for other tools
  const isDailyCardTool = apiEndpoint?.includes('daily-card') || usageApiEndpoint?.includes('daily-card');
  const placeholderText = isDailyCardTool 
    ? formatOracleCardPlaceholder(placeholderQuestion)
    : formatPlaceholder(placeholderQuestion);

  const ensureAuthenticatedUser = useCallback(async () => {
    if (auth?.currentUser) {
      return auth.currentUser;
    }

    try {
      const signedInUser = await signInWithGoogle();
      if (!signedInUser) {
        return null;
      }
      return signedInUser;
    } catch (error) {
      if (error?.code === 'auth/popup-closed-by-user') {
        alert('Sign-in was cancelled. Please try again to unlock premium access.');
      } else if (error?.code === 'auth/cancelled-popup-request') {
        alert('Another sign-in request was in progress. Please try again.');
      } else if (error?.code === 'auth/redirect-cancelled' || error?.message?.includes('redirect')) {
        alert('Sign-in redirect was cancelled or failed. Please try again.');
      } else {
        console.error('Failed to sign in before checkout:', error);
        alert('An error occurred during sign-in. Please try again.');
      }
      return null;
    }
  }, []);

  const fetchUsageMetadata = useCallback(
    async (signal) => {
      console.log('fetchUsageMetadata started');
      if (!csrfToken || csrfLoading) {
        return;
      }

      if ((hasInitialUsage && isPrefetchFreshRef.current) || isPremiumTier) {
        return;
      }

      // Check if signal is already aborted before making the request
      if (signal && signal.aborted) {
        return;
      }

      try {
        setUsageFetchError(null);
        const headers = {
          'X-CSRF-Token': csrfToken,
        };

        if (usageEtagRef.current) {
          headers['If-None-Match'] = usageEtagRef.current;
        }

        const requestInit = {
          method: 'GET',
          headers,
          credentials: 'include',
        };

        if (signal) {
          requestInit.signal = signal;
        }

        const response = await fetch(usageApiEndpoint, requestInit);

        if (signal && signal.aborted) {
          return;
        }

        if (response.status === 304) {
          console.log('fetchUsageMetadata completed (304)');
          return;
        }

        let data = null;
        try {
          console.log('About to call json()');
          data = await response.json();
          console.log('json() returned:', data);
        } catch (parseError) {
          data = null;
        }

        if (response.status === 403) {
          // Handle premium user 403 (expected - premium users have unlimited access)
          if (data?.tier === 'premium' || (data?.error && data.error.includes('Premium users'))) {
            applyUsageSnapshot(null, 'premium');
            console.log('fetchUsageMetadata completed (premium 403)');
            return;
          }
          // Handle CSRF/security validation errors more gracefully
          if (data?.error) {
            console.warn('[fetchUsageMetadata] Security validation error:', data.error);
            // Don't throw - just log and return (usage will be undefined, component will handle)
            setUsageFetchError(`Security validation failed: ${data.error}`);
            return;
          }
          // If we can't parse the response but got 403, assume premium (graceful degradation)
          if (!data) {
            console.warn('[fetchUsageMetadata] 403 with unparseable response, assuming premium');
            applyUsageSnapshot(null, 'premium');
            return;
          }
        }

        if (!response.ok || !data) {
          // Handle 500 errors more gracefully - might be KV unavailable in dev
          if (response.status === 500) {
            console.warn('[fetchUsageMetadata] Server error, using default values for anonymous user');
            // For anonymous users, default to full limit if server error (matches API default: 2)
            if (userTier === 'anonymous') {
              const resetAt = new Date(Date.UTC(
                new Date().getUTCFullYear(),
                new Date().getUTCMonth(),
                new Date().getUTCDate() + 1
              )).toISOString();
              applyUsageSnapshot(
                { limit: 2, remaining: 2, resetAt },
                'anonymous'
              );
              return;
            }
          }
          const errorMessage = data?.error || `Failed to load usage snapshot (${response.status})`;
          throw new Error(errorMessage);
        }

        console.log('About to read ETag');
        const responseEtag = response.headers?.get?.('ETag') ?? null;
        usageEtagRef.current = responseEtag;
        console.log('ETag stored:', responseEtag);

        applyUsageSnapshot(
          {
            limit: data.limit,
            remaining: data.remaining,
            resetAt: data.resetAt,
          },
          data.tier
        );

        setUsageFetchError(null);
        console.log('fetchUsageMetadata completed');
      } catch (error) {
        // Ignore AbortError - this is expected when component unmounts or dependencies change
        if (error.name === 'AbortError' || (signal && signal.aborted)) {
          return;
        }

        console.error('Failed to fetch usage snapshot', error);
        const normalizedError = error instanceof Error
          ? error
          : new Error("Luna cannot reach the temple ledger right now. Please try again.");
        setUsageFetchError(normalizedError);

        if (posthog) {
          posthog.capture('hero_usage_fetch_error', {
            locale,
            tier: userTier,
            message: normalizedError.message,
          });
        }
      }
    },
    [
      applyUsageSnapshot,
      csrfLoading,
      usageApiEndpoint,
      csrfToken,
      hasInitialUsage,
      isPremiumTier,
      locale,
      posthog,
      userTier
    ]
  );

  const { isFresh: isPrefetchFresh } = useUsageFreshness(prefetchedAt, fetchUsageMetadata);

  useEffect(() => {
    isPrefetchFreshRef.current = isPrefetchFresh;
  }, [isPrefetchFresh]);

  useEffect(() => {
    // Use oracle card placeholders for daily card tool, yes/no placeholders for other tools
    const isDailyCardTool = apiEndpoint?.includes('daily-card') || usageApiEndpoint?.includes('daily-card');
    const question = isDailyCardTool 
      ? getRandomOracleCardPlaceholder(locale)
      : getRandomPlaceholderQuestion(locale);
    setPlaceholderQuestion(question);
  }, [locale, apiEndpoint, usageApiEndpoint]);

  useEffect(() => {
    if (!showOracleMovedToast) {
      setOracleMovedToastVisible(false);
      return undefined;
    }

    setOracleMovedToastVisible(true);
    const timeoutId = window.setTimeout(() => {
      setOracleMovedToastVisible(false);
    }, 6000);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [showOracleMovedToast]);

  useEffect(() => {
    if (typeof performance === 'undefined') {
      return undefined;
    }

    const start = performance.now();

    return () => {
      const durationMs = performance.now() - start;

      if (posthog) {
        posthog.capture('hero_section_render_performance', {
          duration_ms: Math.round(durationMs),
          tier: userTier,
          hasInitialUsage,
        });
      }

      if (durationMs > 1000) {
        console.warn('[HeroSection] Slow render detected', { duration_ms: Math.round(durationMs) });
      }
    };
  }, [posthog, userTier, hasInitialUsage]);

  useEffect(() => {
    if (followUpTypingIntervalRef.current) {
      clearInterval(followUpTypingIntervalRef.current);
      followUpTypingIntervalRef.current = null;
    }
    setFollowUpTypingComplete(false);

    if (followUpAnswer && !isFollowUpLoading) {
      setDisplayedFollowUpAnswer('');
      followUpTypingIntervalRef.current = setInterval(() => {
        setDisplayedFollowUpAnswer(prev => {
          const currentIndex = prev.length;
          if (currentIndex >= followUpAnswer.length) {
            clearInterval(followUpTypingIntervalRef.current);
            followUpTypingIntervalRef.current = null;
            setFollowUpTypingComplete(true);
            return prev;
          }
          return prev + followUpAnswer[currentIndex];
        });
      }, 50);
    } else {
      setDisplayedFollowUpAnswer('');
    }

    return () => {
      if (followUpTypingIntervalRef.current) {
        clearInterval(followUpTypingIntervalRef.current);
      }
    };
  }, [followUpAnswer, isFollowUpLoading]);

  useEffect(() => {
    if (!posthog || !deeperInsight || userTier === 'premium') {
      return;
    }

    if (previewLoggedRef.current === deeperInsight) {
      return;
    }

    posthog.capture('oracle_preview_viewed', {
      locale,
      tier: userTier,
      question_length: initialQuestion?.length ?? 0,
      remaining: dailyUsesRemaining,
      answer: yesNoAnswer
    });

    previewLoggedRef.current = deeperInsight;
  }, [deeperInsight, dailyUsesRemaining, initialQuestion, locale, posthog, userTier, yesNoAnswer]);

  useEffect(() => {
    const wasOpen = previousModalOpenRef.current;

    if (!wasOpen && forceUpgradeModal && posthog) {
      posthog.capture('hero_upgrade_modal_open', {
        locale,
        tier: userTier,
        remaining: dailyUsesRemaining,
      });
    }

    previousModalOpenRef.current = forceUpgradeModal;
  }, [dailyUsesRemaining, forceUpgradeModal, locale, posthog, userTier]);

  useEffect(() => {
    if (!forceUpgradeModal) {
      modalErrorLoggedRef.current = false;
      return;
    }

    if (!usageFetchError || modalErrorLoggedRef.current || !posthog) {
      return;
    }

    posthog.capture('hero_upgrade_modal_error', {
      locale,
      tier: userTier,
      message: usageFetchError.message,
    });

    modalErrorLoggedRef.current = true;
  }, [forceUpgradeModal, locale, posthog, usageFetchError, userTier]);

  useEffect(() => {
    if (!csrfToken || csrfLoading) {
      return;
    }

    if ((hasInitialUsage && isPrefetchFresh) || isPremiumTier) {
      return;
    }

    const controller = new AbortController();
    fetchUsageMetadata(controller.signal);

    return () => {
      try {
        if (!controller.signal.aborted) {
          controller.abort();
        }
      } catch (error) {
        // Ignore any errors during cleanup - AbortErrors are expected
        if (error.name !== 'AbortError') {
          console.warn('[HeroSection] Error during cleanup:', error);
        }
      }
    };
  }, [csrfToken, csrfLoading, fetchUsageMetadata, hasInitialUsage, isPrefetchFresh, isPremiumTier]);

  useEffect(() => {
    const pollingEnabledEnv = process.env.NEXT_PUBLIC_HERO_POLLING_ENABLED;
    const pollingEnabled = pollingEnabledEnv === undefined ? true : pollingEnabledEnv !== 'false';

    const teardownPolling = () => {
      if (pollingIntervalRef.current) {
        console.debug('[HeroSection] Clearing polling interval');
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    };

    if (!pollingEnabled || typeof window === 'undefined') {
      teardownPolling();
      return undefined;
    }

    const intervalValue = process.env.NEXT_PUBLIC_HERO_POLLING_INTERVAL_MS;
    const parsedInterval = Number.parseInt(intervalValue ?? '60000', 10);
    const intervalMs = Number.isFinite(parsedInterval) && parsedInterval > 0 ? parsedInterval : 60000;

    if (!csrfToken || csrfLoading || isPremiumTier) {
      teardownPolling();
      return undefined;
    }

    teardownPolling();

    console.debug('[HeroSection] Starting polling interval', { intervalMs });

    pollingIntervalRef.current = window.setInterval(() => {
      console.debug('[HeroSection] Polling usage metadata');
      fetchUsageMetadata();
    }, intervalMs);

    return () => {
      teardownPolling();
    };
  }, [csrfToken, csrfLoading, isPremiumTier, fetchUsageMetadata]);

  useEffect(() => {
    if (!posthog || heroImpressionLoggedRef.current) {
      return;
    }

    posthog.capture('hero_impression', {
      locale,
      tier: userTier,
      remaining: dailyUsesRemaining,
      hasInitialUsage,
    });

    heroImpressionLoggedRef.current = true;
  }, [dailyUsesRemaining, hasInitialUsage, locale, posthog, userTier]);

  const handleRevealAnswer = async (submittedQuestion, options = {}) => {
    // Handle PendulumOracle's format: onSubmit(value, { pendulumType, focusLevel })
    // Handle DreamInterpreter's format: onSubmit(value, { analysisType, dreamQuality, isRecurring, emotion })
    // Or YesNoOracleWidget's format: onSubmit(value)
    const questionToAsk = typeof submittedQuestion === 'string' ? submittedQuestion : initialQuestion;
    const { pendulumType, focusLevel, analysisType, dreamQuality, isRecurring, emotion, emotions } = options;

    if (!questionToAsk.trim()) {
      setError(errorMessages.emptyQuestion || 'Please ask a question first.');
      return;
    }
    if (!csrfToken) {
      setError(errorMessages.security || 'Security token missing. Please refresh and try again.');
      return;
    }
    setInitialQuestion(questionToAsk);
    setIsLoading(true);
    setError(null);
    setYesNoAnswer(null);
    setReframedQuestion('');
    setFollowUpAnswer('');
    setShowFollowUp(false);
    setFollowUpQuestion('');
    setFollowUpTypingComplete(false);
    clearUsageState();
    setConfidence(null);
    setDeeperInsight('');
    setCelebratePremium(false);

    if (posthog) {
      posthog.capture('oracle_question_asked', {
        question_type: apiEndpoint === '/api/oracle/pendulum-yes-no' ? 'pendulum' : 'yes_no_free',
        question_length: questionToAsk.length,
        user_type: posthog.get_distinct_id() ? 'identified' : 'anonymous',
        tier: userTier,
        remaining: dailyUsesRemaining,
        locale,
        ...(pendulumType && { pendulumType }),
        ...(focusLevel && { focusLevel }),
      });
    }

    try {
      // Build request body - pendulum and dream-interpreter endpoints need additional params
      const requestBody = {
        question: questionToAsk,
        userId: posthog?.get_distinct_id(),
        locale
      };
      
      // Add pendulum-specific params if provided
      if (apiEndpoint === '/api/oracle/pendulum-yes-no') {
        if (pendulumType) {
          requestBody.pendulumType = pendulumType;
        }
        if (focusLevel) {
          requestBody.focusLevel = focusLevel;
        }
      }
      
      // Add dream-interpreter-specific params if provided
      if (apiEndpoint === '/api/oracle/dream-interpreter') {
        if (analysisType) {
          requestBody.analysisType = analysisType;
        }
        if (dreamQuality) {
          requestBody.dreamQuality = dreamQuality;
        }
        if (isRecurring !== undefined) {
          requestBody.isRecurring = isRecurring;
        }
        // Handle emotions: prefer array (new) over single emotion (legacy)
        if (emotions && Array.isArray(emotions) && emotions.length > 0) {
          requestBody.emotions = emotions;
        } else if (emotion) {
          // Legacy support: convert single emotion to array
          requestBody.emotions = [emotion];
        }
        // For dream interpreter, the question field is actually the dream description
        requestBody.dreamDescription = questionToAsk;
        delete requestBody.question;
      }
      
      // Add daily-card-specific params if provided
      if (apiEndpoint === '/api/oracle/daily-card') {
        const { spreadType, relationshipContext } = options;
        if (spreadType) {
          requestBody.spreadType = spreadType;
        }
        if (relationshipContext) {
          requestBody.relationshipContext = relationshipContext;
        }
        // Question is optional for daily-card
        if (questionToAsk) {
          requestBody.question = questionToAsk;
        }
      }

      const response = await fetch(apiEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken
        },
        credentials: 'include',
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        let errorMsg = `Oracle Error: ${response.statusText}`;
        if (response.status === 429) {
          errorMsg = errorMessages.rateLimit || 'Too many requests, please wait a moment and try again.';
          try {
            const errorData = await response.json();
            if (
              errorData?.error &&
              (errorData.error.includes('Daily free limit reached') ||
                errorData.error.includes('Daily usage limit reached for your account.'))
            ) {
              if (errorData?.usage) {
                applyUsageSnapshot(errorData.usage, userTier);
              }
              handleUsageLimitReached();
              setIsLoading(false);
              return;
            }
          } catch (parseError) {
            console.error('Failed to parse 429 error response as JSON', parseError);
          }
        } else {
          try {
            // Clone the response before reading to allow fallback to text if JSON fails
            const clonedResponse = response.clone();
            try {
              const errorData = await clonedResponse.json();
              // Safely extract error message, avoiding any PostHog trace ID access
              if (errorData && typeof errorData === 'object') {
                errorMsg = errorData.error || errorData.message || errorMsg;
              }
            } catch (jsonError) {
              // If JSON parsing fails, try to get text response
              const textError = await response.text();
              if (textError) {
                errorMsg = `Oracle Error: ${textError.substring(0, 200)}`;
              }
            }
          } catch (parseError) {
            console.error('Failed to parse error response', parseError);
            // If all else fails, use the status text
            errorMsg = `Oracle Error: ${response.statusText || 'Unknown error'}`;
          }
        }
        throw new Error(errorMsg);
      }

      let data;
      try {
        data = await response.json();
      } catch (jsonError) {
        console.error('Failed to parse response as JSON', jsonError);
        setError('Invalid response from oracle. Please try again.');
        return;
      }

      // Safely extract response data with null checks
      if (!data || typeof data !== 'object') {
        setError('Invalid response format from oracle. Please try again.');
        return;
      }

      // Handle discriminated union response (ok: true/false)
      if (data.ok === false) {
        // Error response
        const errorMessage = data.error || data.message || 'Oracle Error';
        setError(errorMessage);
        return;
      }

      // Success response (ok: true or legacy format without ok field)
      // Guard all array operations with Array.isArray checks
      const safeAnswer = typeof data.answer === 'string' ? data.answer : (Math.random() < 0.5 ? 'YES' : 'NO');
      const safeExplanation = typeof data.explanation === 'string' ? data.explanation : '';
      const safeDeeperInsight = typeof data.deeperInsight === 'string' ? data.deeperInsight : (typeof data.deeperInsights === 'string' ? data.deeperInsights : '');
      const safeConfidence = typeof data.confidence === 'number' ? data.confidence : null;
      const safeUsage = data.usage && typeof data.usage === 'object' ? data.usage : null;
      const safeTier = typeof data.tier === 'string' ? data.tier : userTier;

      setYesNoAnswer(safeAnswer);
      setReframedQuestion(safeExplanation);
      setDeeperInsight(safeDeeperInsight);
      setConfidence(safeConfidence);
      setCelebratePremium(Boolean(data.celebrate));
      applyUsageSnapshot(safeUsage, safeTier);

      // Note: We no longer show aggressive modal on Q3
      // The widget will show graceful Temple CTA instead
      if (!isPremiumTier && typeof data?.usage?.remaining === 'number' && data.usage.remaining === 0) {
        handleUsageLimitReached();
        // Don't request upgrade modal - let widget handle graceful messaging
      }

      setShowFollowUp(true);
    } catch (err) {
      console.error('Network or fetch error', err);
      
      // Safely extract error message, preventing any .map() calls on undefined
      let errorMessage = 'Could not connect to the ether.';
      try {
        if (err && typeof err === 'object') {
          if (typeof err.message === 'string' && err.message.length > 0) {
            errorMessage = err.message;
          } else if (typeof err.toString === 'function') {
            errorMessage = err.toString();
          }
        } else if (typeof err === 'string') {
          errorMessage = err;
        }
      } catch (extractError) {
        console.error('Failed to extract error message', extractError);
        errorMessage = errorMessages.generic || 'Could not connect to the ether.';
      }
      
      setError(errorMessage);
      setYesNoAnswer(null);
    } finally {
      setIsLoading(false);
    }
  };

  const handleFollowUp = async () => {
    if (!followUpQuestion.trim()) {
      setFollowUpError(errorMessages.emptyFollowUp || 'Please ask a follow-up question.');
      return;
    }
    if (!csrfToken) {
      setFollowUpError(errorMessages.security || 'Security token missing. Please refresh and try again.');
      return;
    }
    setIsFollowUpLoading(true);
    setFollowUpError(null);
    setFollowUpAnswer('');
    setFollowUpTypingComplete(false);

    if (posthog) {
      posthog.capture('oracle_follow_up_asked', {
        question_length: followUpQuestion.length,
        user_type: posthog.get_distinct_id() ? 'identified' : 'anonymous',
        tier: userTier,
        remaining: dailyUsesRemaining,
        locale,
      });
    }

    try {
      const response = await fetch('/api/answer-follow-up', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken
        },
        credentials: 'include',
        body: JSON.stringify({
          originalQuestion: initialQuestion,
          yesNoAnswer,
          reframedQuestion,
          followUpQuestion,
          dailyUsesRemaining,
          userId: posthog?.get_distinct_id(),
          locale
        })
      });

      if (!response.ok) {
        let errorMsg = `Oracle Error: ${response.statusText}`;
        if (response.status === 429) {
          errorMsg = errorMessages.followUpRateLimit || 'Too many requests, please wait a moment and try again.';
          try {
            const errorData = await response.json();
            if (
              errorData?.error &&
              (errorData.error.includes('Daily free limit reached') ||
                errorData.error.includes('Daily usage limit reached for your account.'))
            ) {
              if (errorData?.usage) {
                applyUsageSnapshot(errorData.usage, userTier);
              }
              handleUsageLimitReached();
              setIsFollowUpLoading(false);
              return;
            }
          } catch (parseError) {
            console.error('Failed to parse follow-up 429 error as text', parseError);
          }
        } else {
          try {
            const errorData = await response.json();
            errorMsg = errorData?.error || errorMsg;
          } catch (parseError) {
            console.error('Failed to parse follow-up error response as JSON', parseError);
          }
        }
        throw new Error(errorMsg);
      }

      const data = await response.json();
      setFollowUpAnswer(data.followUpAnswer ?? '');
    } catch (err) {
      console.error('Follow-up fetch error', err);
      setFollowUpError(err.message || errorMessages.followUpGeneric || 'The Oracle remains silent on this.');
    } finally {
      setIsFollowUpLoading(false);
    }
  };

  const handleUsageLimitReached = useCallback(() => {
    markLimitReached();
    if (posthog) {
      posthog.capture('hero_usage_limit_reached', {
        locale,
        tier: userTier,
        remaining: dailyUsesRemaining,
      });
    }
  }, [dailyUsesRemaining, locale, markLimitReached, posthog, userTier]);

  const handleUpgradeNavigation = useCallback(async () => {
    if (posthog) {
      posthog.capture('upgrade_modal_cta_clicked', {
        source: 'hero_widget',
        locale,
      });
    }

    if (!DECEMBER_PROMO_PRICE_ID) {
      dismissUpgradeModal();
      window.location.assign(`/${locale}/pricing`);
      return;
    }

    const authenticatedUser = await ensureAuthenticatedUser();

    if (!authenticatedUser) {
      return;
    }

    try {
      await startCheckout(authenticatedUser.uid, DECEMBER_PROMO_PRICE_ID, locale);
    } catch (error) {
      console.error('Failed to initiate checkout from hero upgrade modal:', error);
    }
  }, [dismissUpgradeModal, ensureAuthenticatedUser, locale, posthog, startCheckout]);

  const handleUpgradeIntent = useCallback((reason = 'unknown') => {
    if (posthog) {
      posthog.capture('oracle_preview_unlock_clicked', {
        locale,
        tier: userTier,
        remaining: dailyUsesRemaining,
        reason
      });
    }

    // Note: We no longer trigger modal for deeper-insight-preview
    // Deeper insight is now always shown in full, with Temple CTA instead
    // This handler is kept for other upgrade intents (e.g., from header)
    if (reason === 'deeper-insight-preview') {
      // No longer showing modal - user can join Temple via CTA
      return;
    }

    handleUsageLimitReached();
  }, [dailyUsesRemaining, handleUsageLimitReached, locale, posthog, userTier]);

  const handleUpgradeModalClose = useCallback(() => {
    if (posthog) {
      posthog.capture('hero_upgrade_modal_dismiss', {
        locale,
        tier: userTier,
        remaining: dailyUsesRemaining,
      });
    }
    resetCheckout();
    dismissUpgradeModal();
    fetchUsageMetadata();
  }, [dailyUsesRemaining, dismissUpgradeModal, fetchUsageMetadata, locale, posthog, resetCheckout, userTier]);

  const handleUpgradeModalUpgrade = useCallback(async () => {
    if (posthog) {
      posthog.capture('hero_upgrade_modal_upgrade', {
        locale,
        tier: userTier,
      });
    }
    await handleUpgradeNavigation();
  }, [handleUpgradeNavigation, locale, posthog, userTier]);

  const followUpAfterByRemaining = {
    2: followUpAfter?.first,
    1: followUpAfter?.second,
    0: followUpAfter?.final
  };

  return (
    <div className="container mx-auto px-4 py-16 md:py-24">
      {oracleMovedToastVisible ? (
        <div className="pointer-events-none fixed inset-x-0 top-24 z-50 flex justify-center px-4">
          <div
            className="pointer-events-auto w-full max-w-sm rounded-xl border border-[#6A3683]/40 bg-[#100e23]/95 p-4 shadow-2xl shadow-[#6A3683]/20 backdrop-blur"
            data-testid="oracle-moved-toast"
          >
            <div className="flex items-start gap-3">
              <div className="flex-1">
                <p
                  className="text-sm font-semibold text-[#FBFBFE]"
                  style={{ fontFamily: 'var(--font-marcellus)' }}
                >
                  {oracleMovedToast.title || t('oracleMovedToast.title', { defaultValue: 'The dashboard Yes/No tool now lives here' })}
                </p>
                <p className="mt-1 text-sm text-gray-300">
                  {oracleMovedToast.description || t('oracleMovedToast.description', { defaultValue: "Use Luna's homepage widget for instant answers." })}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setOracleMovedToastVisible(false)}
                aria-label={oracleMovedToast.closeLabel || t('oracleMovedToast.closeLabel', { defaultValue: 'Dismiss' })}
                className="text-gray-400 transition hover:text-gray-200"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      ) : null}

      <div className="flex flex-col md:flex-row md:items-start gap-6 md:gap-8 lg:gap-12">
        <HeroStaticCopy
          badge={badge}
          headline={headline}
          subheadline={subheadline}
          metaItems={metaItems}
          metaLabel={metaLabel}
        />

        <div className="w-full md:w-3/5 lg:w-3/5 mt-12 md:mt-0 flex flex-col items-center space-y-3 p-4 md:p-5 bg-[#100e23]/70 rounded-xl border border-[#6A3683]/40 shadow-xl shadow-[#6A3683]/10">
        {/* Title with badge inline (premium) or free title */}
        {isPremiumTier ? (
          <div className="flex items-center justify-center gap-2 mb-2">
            <TempleKeeperBadge tier="gold" disablePortal className="shrink-0 scale-75" />
            <h3
              className="text-lg md:text-xl font-semibold text-[#FBFBFE] text-center"
              style={{ fontFamily: 'var(--font-marcellus)' }}
            >
              {widgetTitlePremiumText}
            </h3>
          </div>
        ) : (
          <h3
            className="text-lg md:text-xl font-semibold text-[#FBFBFE] mb-2 text-center"
            style={{ fontFamily: 'var(--font-marcellus)' }}
          >
            {widgetTitle}
          </h3>
        )}


          {usageFetchError ? (
            <div
              role="alert"
              className="w-full rounded-lg border border-red-500/40 bg-red-500/10 px-4 py-3 text-sm text-red-200"
            >
              <p className="leading-relaxed">
                {usageFetchError.message || "Luna cannot reach the temple ledger right now. Please try again."}
              </p>
              <div className="mt-3 flex justify-end">
                <button
                  type="button"
                  onClick={() => fetchUsageMetadata()}
                  className="rounded-full border border-red-200/40 px-4 py-2 text-xs font-semibold uppercase tracking-[0.2em] text-red-100 transition hover:border-red-200 hover:text-white"
                >
                  Retry
                </button>
              </div>
            </div>
          ) : null}

          {(() => {
            // Capitalize component name for React (must start with uppercase)
            // React treats lowercase tags as DOM elements, uppercase as components
            const WidgetComponent = customWidget || YesNoOracleWidget;
            return (
              <WidgetComponent
                question={initialQuestion}
                onQuestionChange={(value) => {
                  setInitialQuestion(value);
                  setError(null);
                }}
                onSubmit={handleRevealAnswer}
                isLoading={isLoading}
                isDisabled={isFollowUpLoading || dailyLimitReached || csrfLoading}
                answer={yesNoAnswer}
                explanation={reframedQuestion}
                deeperInsight={deeperInsight}
                confidence={confidence}
                usageMetadata={usageMetadata}
                isPremium={userTier === 'premium'}
                celebrate={celebratePremium}
                onLimitReached={handleUsageLimitReached}
                onUpgradeIntent={handleUpgradeIntent}
                error={error}
                submitLabel={cta.primary || 'Reveal Oracle Whisper'}
                loadingLabel={cta.primaryLoading || 'Consulting the Oracle...'}
                placeholderText={placeholderText}
              />
            );
          })()}

          {/* Removed aggressive limit modal - graceful messaging now handled in widget via TempleSignupCTA */}

          {showFollowUp && !isLoading && (
            <div className="w-full space-y-3 mt-4 animate-fade-in">
              {followUpAnswer && followUpTypingComplete ? (
                <>
                  <div className="w-full p-3 rounded-lg bg-[#1f1d33] text-gray-200 border border-[#6A3683]/60 mt-3 animate-fade-in shadow-md shadow-[#6A3683]/10 min-h-[60px] flex items-center space-x-3">
                    <div className="shrink-0">
                      <Image 
                        src="/images/oracle-luna.png" 
                        alt="Oracle Luna" 
                        width={32} 
                        height={32} 
                        className="rounded-full"
                        priority // Small but appears above fold, prioritize for LCP
                      />
                    </div>
                    <div className="grow flex items-center justify-center">
                      <p className="italic text-center text-sm w-full">
                        {sanitizeUserInput(displayedFollowUpAnswer)}
                      </p>
                    </div>
                  </div>

                  {/* Removed follow-up pricing CTAs - Temple CTAs now handled in widget */}
                </>
              ) : null}

              {(followUpAnswer || isFollowUpLoading) && !followUpTypingComplete && (
                <div className="w-full p-3 rounded-lg bg-[#1f1d33] text-gray-200 border border-[#6A3683]/60 mt-3 animate-fade-in shadow-md shadow-[#6A3683]/10 min-h-[60px] flex items-center space-x-3">
                  <div className="shrink-0">
                    <Image src="/images/oracle-luna.png" alt="Oracle Luna" width={32} height={32} className="rounded-full" />
                  </div>
                  <div className="grow flex items-center justify-center">
                    {isFollowUpLoading && !followUpAnswer ? (
                      <span className="animate-blink text-xl">|</span>
                    ) : (
                      <p className="italic text-center text-sm w-full">
                        {sanitizeUserInput(displayedFollowUpAnswer)}
                        {!followUpTypingComplete && <span className="animate-blink">|</span>}
                      </p>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      {/* Upgrade Modal removed - using Temple-level conversion instead */}
    </div>
  );
};

const HeroSection = ({ content, initialUsage, initialTier, initialQuestion, prefetchedAt, showOracleMovedToast, customWidget, apiEndpoint, usageApiEndpoint, widgetTitleFree, widgetTitlePremium }) => (
  <HeroUsageProvider initialUsage={initialUsage} initialTier={initialTier}>
    <HeroSectionContent
      content={content}
      initialQuestion={initialQuestion}
      prefetchedAt={prefetchedAt}
      showOracleMovedToast={showOracleMovedToast}
      customWidget={customWidget}
      apiEndpoint={apiEndpoint}
      usageApiEndpoint={usageApiEndpoint}
      widgetTitleFree={widgetTitleFree}
      widgetTitlePremium={widgetTitlePremium}
    />
  </HeroUsageProvider>
);

HeroSection.propTypes = {
  content: PropTypes.object,
  initialUsage: PropTypes.oneOfType([usageMetadataPropType, PropTypes.oneOf([null])]),
  initialTier: PropTypes.oneOf(['anonymous', 'free', 'premium']),
  initialQuestion: PropTypes.string,
  prefetchedAt: PropTypes.oneOfType([PropTypes.number, PropTypes.oneOf([null])]),
  showOracleMovedToast: PropTypes.bool,
  customWidget: PropTypes.elementType, // NEW: Optional custom widget component
  apiEndpoint: PropTypes.string, // NEW: Optional custom API endpoint
  usageApiEndpoint: PropTypes.string, // NEW: Optional custom usage API endpoint
  widgetTitleFree: PropTypes.string, // NEW: Optional widget title for free users
  widgetTitlePremium: PropTypes.string, // NEW: Optional widget title for premium users
};

HeroSection.defaultProps = {
  content: {},
  initialUsage: null,
  initialTier: 'anonymous',
  initialQuestion: '',
  prefetchedAt: null,
  showOracleMovedToast: false,
  customWidget: null, // NEW: Default to null (uses YesNoOracleWidget)
};

export default HeroSection;